/**
 * Owner: Masa Bayu
 * File: Icon.tsx
 * Date:Time: 2026-09-18 11:43
 * Description: UI-01 QxIcon — Native Open Web SVG catalog (incl. plus · account nav).
 */

import type { ReactElement, SVGProps } from 'react';

export type QxIconName =
  | 'home'
  | 'search'
  | 'library'
  | 'cart'
  | 'bookmark'
  | 'play'
  | 'settings'
  | 'share'
  | 'discover'
  | 'account'
  | 'plus'
  | 'close'
  | 'back'
  | 'menu'
  | 'filter'
  | 'toc'
  | 'fullscreen'
  | 'page-prev'
  | 'page-next'
  | 'circle'
  | 'live';

export type QxIconSize = 'sm' | 'md' | 'lg';

export interface QxIconProps {
  readonly name: QxIconName;
  readonly size?: QxIconSize;
  readonly title?: string;
  readonly className?: string;
}

type PathFn = (props: SVGProps<SVGSVGElement>) => ReactElement;

const stroke = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

function svg(
  children: ReactElement | readonly ReactElement[],
  props: SVGProps<SVGSVGElement>,
): ReactElement {
  return (
    <svg viewBox="0 0 24 24" width="1em" height="1em" aria-hidden={props['aria-hidden']} {...props}>
      {children}
    </svg>
  );
}

const PATHS: Record<QxIconName, PathFn> = {
  home: (p) =>
    svg(
      <>
        <path {...stroke} d="M4 10.5 12 4l8 6.5V20a1 1 0 0 1-1 1h-5v-6H10v6H5a1 1 0 0 1-1-1v-9.5Z" />
      </>,
      p,
    ),
  search: (p) =>
    svg(
      <>
        <circle {...stroke} cx="11" cy="11" r="6.5" />
        <path {...stroke} d="m16.5 16.5 4 4" />
      </>,
      p,
    ),
  library: (p) =>
    svg(
      <>
        <path {...stroke} d="M5 4h4v16H5zM11 4h4v16h-4zM17 6.5 20.5 5v14L17 17.5z" />
      </>,
      p,
    ),
  cart: (p) =>
    svg(
      <>
        <path {...stroke} d="M3.5 5h2l1.2 10.5h11.3L20.5 8H7" />
        <circle {...stroke} cx="9.5" cy="19.5" r="1.25" />
        <circle {...stroke} cx="16.5" cy="19.5" r="1.25" />
      </>,
      p,
    ),
  bookmark: (p) =>
    svg(<path {...stroke} d="M7 4.5h10v16l-5-3.2-5 3.2v-16Z" />, p),
  play: (p) =>
    svg(<path {...stroke} d="M8.5 6.5v11l9-5.5-9-5.5Z" />, p),
  settings: (p) =>
    svg(
      <>
        <circle {...stroke} cx="12" cy="12" r="3" />
        <path
          {...stroke}
          d="M12 3.5v2.2M12 18.3V20.5M4.7 7.2l1.9 1.1M17.4 15.7l1.9 1.1M4.7 16.8l1.9-1.1M17.4 8.3l1.9-1.1"
        />
      </>,
      p,
    ),
  share: (p) =>
    svg(
      <>
        <circle {...stroke} cx="18" cy="6" r="2.25" />
        <circle {...stroke} cx="6" cy="12" r="2.25" />
        <circle {...stroke} cx="18" cy="18" r="2.25" />
        <path {...stroke} d="m8.1 10.9 7.8-3.8M8.1 13.1l7.8 3.8" />
      </>,
      p,
    ),
  discover: (p) =>
    svg(
      <>
        <circle {...stroke} cx="12" cy="12" r="8" />
        <path {...stroke} d="m14.8 9.2-1.6 4.4-4.4 1.6 1.6-4.4 4.4-1.6Z" />
      </>,
      p,
    ),
  account: (p) =>
    svg(
      <>
        <circle {...stroke} cx="12" cy="9" r="3.25" />
        <path {...stroke} d="M5.5 19.5c1.6-3 3.9-4.5 6.5-4.5s4.9 1.5 6.5 4.5" />
      </>,
      p,
    ),
  plus: (p) =>
    svg(
      <>
        <path {...stroke} d="M12 5v14M5 12h14" />
      </>,
      p,
    ),
  close: (p) =>
    svg(
      <>
        <path {...stroke} d="M6.5 6.5 17.5 17.5M17.5 6.5 6.5 17.5" />
      </>,
      p,
    ),
  back: (p) =>
    svg(<path {...stroke} d="M14.5 5.5 8 12l6.5 6.5M8 12h10" />, p),
  menu: (p) =>
    svg(
      <>
        <path {...stroke} d="M5 7h14M5 12h14M5 17h14" />
      </>,
      p,
    ),
  filter: (p) =>
    svg(<path {...stroke} d="M4.5 6h15l-5.5 7v5.5l-4-2V13L4.5 6Z" />, p),
  toc: (p) =>
    svg(
      <>
        <path {...stroke} d="M7 6h12M7 12h12M7 18h8M4.5 6h.01M4.5 12h.01M4.5 18h.01" />
      </>,
      p,
    ),
  fullscreen: (p) =>
    svg(
      <>
        <path {...stroke} d="M8 4.5H4.5V8M16 4.5h3.5V8M8 19.5H4.5V16M16 19.5h3.5V16" />
      </>,
      p,
    ),
  'page-prev': (p) =>
    svg(<path {...stroke} d="M14.5 5.5 8 12l6.5 6.5M8 12h10" />, p),
  'page-next': (p) =>
    svg(<path {...stroke} d="M9.5 5.5 16 12l-6.5 6.5M6 12h10" />, p),
  circle: (p) =>
    svg(
      <>
        <circle {...stroke} cx="12" cy="12" r="8.25" />
        <circle cx="12" cy="8.15" r="1.35" fill="currentColor" stroke="none" />
        <circle cx="8.7" cy="14.15" r="1.35" fill="currentColor" stroke="none" />
        <circle cx="15.3" cy="14.15" r="1.35" fill="currentColor" stroke="none" />
      </>,
      p,
    ),
  live: (p) =>
    svg(
      <>
        <rect {...stroke} x="3.25" y="6.25" width="14.5" height="11.5" rx="2.25" />
        <path d="M9 9.4v5.2l4.6-2.6Z" fill="currentColor" stroke="none" />
      </>,
      p,
    ),
};

export function QxIcon({ name, size = 'md', title, className }: QxIconProps) {
  const Path = PATHS[name];
  const labelled = title !== undefined && title.length > 0;
  const classes = ['qx-icon', `qx-icon--${size}`, className].filter(Boolean).join(' ');
  return (
    <span className={classes} data-icon={name}>
      {Path({
        'aria-hidden': labelled ? undefined : true,
        role: labelled ? 'img' : undefined,
        'aria-label': labelled ? title : undefined,
      })}
    </span>
  );
}

export function iconNameForNavKey(key: string): QxIconName | null {
  const map: Record<string, QxIconName> = {
    home: 'home',
    discover: 'discover',
    search: 'search',
    library: 'library',
    'my-books': 'library',
    cart: 'cart',
    upload: 'plus',
    profile: 'account',
    account: 'account',
    books: 'library',
    magazines: 'library',
  };
  return map[key] ?? null;
}
